// admin.kpi.api.ts
import { AxiosResponse } from 'axios';
import { Request } from '../../../../api';

interface AppMetrics {
    total_users: number;
    versions: Record<string, number>;
    error?: string;
    note?: string;
}

interface MobileMetricsResponse {
    iosMetrics: {
        coach_app: AppMetrics;
        athlete_app: AppMetrics;
    };
    androidMetrics: {
        athlete_app: AppMetrics;
    };
}

interface ShopMetrics {
    sessions: number;
    completedCheckouts: number;
    dropOffRate: number;
    averageSessionLengthSeconds: number;
    averageSessionLengthMinutes: number;
    startDate: string;
    endDate: string;
}

interface UploadMetricsPeriod {
    totalUploads: number;
    totalUploadsWithErrors: number;
    totalSuccessfulUploads: number;
    successRate: number;
    accurateData: number;
    accurateDataPercentage: number;
    nonHumanData: number;
    nonHumanDataPercentage: number;
    startDate?: string;
    endDate?: string;
}

interface UploadMetrics {
    allTime: UploadMetricsPeriod;
    last7Days: UploadMetricsPeriod;
    maxHumanSpeedThreshold: number;
}

interface UploadTrendDataPoint {
    date: string;
    timestamp: string;
    successful: number;
    unsuccessful: number;
}

interface UploadUserDetail {
    user_name: string;
    org_name: string;
    created_at: string;
    error?: string;
}

interface UploadTrendResponse {
    trend: UploadTrendDataPoint[];
    successful_users: UploadUserDetail[];
    unsuccessful_users: UploadUserDetail[];
}

interface EamDailyDataPoint {
    day: string;
    dau?: number;
    submissions?: number;
    dau_any_form?: number;
}

interface EamRpeAverage {
    day: string;
    avg_rpe: number;
    avg_duration: number;
}

interface EamRpeLoad {
    day: string;
    total_rpe_load: number;
}

interface EamBodyPartsAvg {
    day: string;
    avg_body_parts: number;
}

interface EamBodyPartDistribution {
    parts_selected: number;
    submissions: number;
}

interface EamMostReportedBodyPart {
    display_name: string;
    reports: number;
}

interface EamPainVsSoreness {
    day: string;
    type: string;
    selections: number;
}

interface EamReadinessScore {
    day: string;
    avg_readiness: number;
    avg_score: number;
    avg_sleep_quantity: number;
    avg_sleep_quality: number;
}

interface EamZeroBodyPartSubmission {
    day: string;
    zero_part_submissions: number;
    total_submissions: number;
    pct_zero_part: number;
}

interface EamMetricsResponse {
    dailyActiveAthletes: {
        rpe: EamDailyDataPoint[];
        readiness: EamDailyDataPoint[];
        painSoreness: EamDailyDataPoint[];
    };
    dailySubmissionVolume: {
        rpe: EamDailyDataPoint[];
        readiness: EamDailyDataPoint[];
        painSoreness: EamDailyDataPoint[];
    };
    unifiedDailyActiveAthletes: EamDailyDataPoint[];
    rpeLoadMetrics: {
        averages: EamRpeAverage[];
        totalLoad: EamRpeLoad[];
    };
    painSorenessDetail: {
        avgBodyPartsPerSubmission: EamBodyPartsAvg[];
        bodyPartsDistribution: EamBodyPartDistribution[];
        mostReportedBodyParts: EamMostReportedBodyPart[];
        painVsSorenessMix: EamPainVsSoreness[];
    };
    readinessScores: EamReadinessScore[];
    dataQualityChecks: {
        zeroBodyPartSubmissions: EamZeroBodyPartSubmission[];
    };
    generatedAt: string;
    dateRange: {
        start: string;
        end: string;
        days: number;
    };
}

interface AmplitudeSeriesValue {
    setId: string;
    value: number;
}

interface AmplitudeSeriesMeta {
    segment: string;
    segments: string[];
    segmentIndex: number;
    eventIndex: number;
}

interface AmplitudeChartData {
    series: AmplitudeSeriesValue[][];
    seriesCollapsed: AmplitudeSeriesValue[][];
    seriesLabels: string[];
    seriesMeta: AmplitudeSeriesMeta[];
    xValues: string[];
}

interface AmplitudeChartResponse {
    data: AmplitudeChartData;
    timeComputed: number;
    wasCached: boolean;
}

const adminGetEamMetrics = (days: number = 30): Promise<AxiosResponse<EamMetricsResponse>> => {
    return Request.get(`/admin/kpi/eam?days=${days}`);
};

const adminGetUploadTrend = (): Promise<AxiosResponse<UploadTrendResponse>> => {
    return Request.get('/admin/kpi/upload-trend');
};

const adminGetMobileMetrics = (): Promise<AxiosResponse<MobileMetricsResponse>> => {
    return Request.get('/admin/kpi/mobile');
};

const adminGetShopMetrics = (): Promise<AxiosResponse<ShopMetrics>> => {
    return Request.get('/admin/kpi/shop');
};

const adminGetUploadMetrics = (): Promise<AxiosResponse<UploadMetrics>> => {
    return Request.get('/admin/kpi/uploads');
};

const adminGetAmplitudeChart = (
    chartId: string,
): Promise<AxiosResponse<AmplitudeChartResponse>> => {
    return Request.get(`/admin/kpi/amplitude/chart/${chartId}`);
};

export {
    adminGetMobileMetrics,
    adminGetShopMetrics,
    adminGetUploadMetrics,
    adminGetUploadTrend,
    adminGetEamMetrics,
    adminGetAmplitudeChart,
};

export type {
    MobileMetricsResponse,
    AppMetrics,
    ShopMetrics,
    UploadMetrics,
    UploadMetricsPeriod,
    UploadTrendDataPoint,
    UploadUserDetail,
    UploadTrendResponse,
    EamMetricsResponse,
    EamDailyDataPoint,
    EamRpeAverage,
    EamReadinessScore,
    EamMostReportedBodyPart,
    AmplitudeChartResponse,
    AmplitudeChartData,
};
